import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Typography,
  FormGroup,
  Checkbox,
  Grid,
  Button,
  Card,
  CardContent,
  CircularProgress,
} from "@mui/material";
import NavigationBar from "../../Layout/Header/header";
import Footer from "../../Layout/Footer/footer";
import CustomerReqImg from "../../images/CustomerReqImg.svg";
import { Link, useParams, useLocation, useNavigate } from "react-router-dom";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { AuthContext } from "../../context/authcontext";
import { API_CONFIG } from "../../api/apiUrl";
import { PlotService, PlotDetails, ServiceType, } from "../../types/CustomerRequirement";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import toast from "react-hot-toast";
import { ContractorType } from "../../types/Booking";
import PaymentIcon from "@mui/icons-material/Payments";
import Uploadicon from "../../images/uploadicon.svg";
import { Modal, TextField, IconButton, Chip, MenuItem } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";



const CustomerRequirement = () => {
  const navigate = useNavigate();
  const { plotId } = useParams<{ plotId: string }>();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const auth = useContext(AuthContext) as any;
  const loggedInUser = auth.user;
  const { userProfile, loading, authorities, setSelectedPlotId } = auth;

  const [services, setServices] = useState<PlotService[]>([]);
  const [allServices, setAllServices] = useState<ServiceType[]>([]);
  const [unselected, setUnselected] = useState<ServiceType[]>([]);
  const [plot, setPlot] = useState<PlotDetails | null>(null);
  const [fetching, setFetching] = useState(true);
  const { selectedService } = location.state || {};

  const [planCards, setPlanCards] = useState<any[]>([]); 
  const [selectedPlan, setSelectedPlan] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedCard, setExpandedCard] = useState<number | null>(null);
  const [plotMainData, setPlotMainData] = useState<any>(null);
  const [openPaymentModal, setOpenPaymentModal] = useState(false);
  const [bookingAmount, setBookingAmount] = useState("");
  const [paymentDate, setPaymentDate] = useState("");
  const [paymentMode, setPaymentMode] = useState("");
  const [paymentReferenceId, setPaymentReferenceId] = useState("");
  const [files, setFiles] = React.useState<{ file: File; url: string; previewUrl: string }[]>([]);
  const [uploadSuccess, setUploadSuccess] = React.useState(false);
  const [isUploading, setIsUploading] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [todayDate, setTodayDate] = useState(new Date().toISOString().split("T")[0]);


  useEffect(() => {
    if (!plotId) return;
    const token = localStorage.getItem("id_token");
    if (!token) return;
    const config = { headers: { Authorization: `Bearer ${token}` } };
    let status = searchParams.get("status") || (location.state && location.state.status) || "REQUESTED";
    axios
      .get(`${API_CONFIG.PLOT_SERVICE_REQS_MAIN}?page=0&size=50&status=${status}&search=`, config)
      .then((res) => {
        const all = Array.isArray(res.data) ? res.data : [];
        const found = all.find((item) => String(item.plot?.id) === String(plotId));
        // console.log("[DEBUG] plotMainData found:", found);
        setPlotMainData(found || null);
        const cards = found?.plotRateCards || [];
        setPlanCards(cards);
        const approved = cards.find((c: any) => c.isApproved === true);
        if (approved) setSelectedPlan(approved);
        else setSelectedPlan(null);
      })
      .catch(() => {
        setPlanCards([]);
        setSelectedPlan(null);
        setPlotMainData(null);
      });
  }, [plotId, searchParams, location.state?.status]);

  useEffect(() => {
    if (location.state?.plotRateCards) {
      setPlanCards(location.state.plotRateCards);
    }
  }, [location.state?.plotRateCards]);

  useEffect(() => {

    if (location.state?.selectedTab === "In Progress" && location.state?.selectedPlan) {
      setSelectedPlan(location.state.selectedPlan);
    }
  }, [location.state]);

  // Update today's date every day
  useEffect(() => {
    const updateTodayDate = () => {
      setTodayDate(new Date().toISOString().split("T")[0]);
    };
    
    // Update immediately
    updateTodayDate();
    
    // Update every day at midnight
    const interval = setInterval(updateTodayDate, 24 * 60 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, []);

  // Set payment date to today whenever modal opens
  useEffect(() => {
    if (openPaymentModal) {
      setPaymentDate(todayDate);
    }
  }, [openPaymentModal, todayDate]);

  const isRM: boolean = authorities.includes("ROLE_RM");
  const isProvider: boolean = authorities.includes("ROLE_PROVIDER");
  const isUser: boolean = authorities.includes("ROLE_USER");

  useEffect(() => {
    if (loading || !plotId) return;
    fetchAll();
    async function fetchAll() {
      setFetching(true);
      setServices([]);
      setAllServices([]);
      setUnselected([]);
      setPlot(null);
      const token = localStorage.getItem("id_token");
      if (!token) return setFetching(false);
      const contractorId = selectedService?.id || 1;
      const config = { headers: { Authorization: `Bearer ${token}` } };

      let fetchedServices: PlotService[] = [];
      try {
        const res = await axios.get(`${API_CONFIG.PLOT_SERVICE_REQS}?plotId.equals=${plotId}&page=0&size=20`, config);
        fetchedServices = res.data || [];
        setServices(fetchedServices);
      } catch { setServices([]); }

      let fetchedFiles: any[] = [];
      try {
        const res = await axios.get(`${API_CONFIG.PLOT_FILES}?plotId.equals=${plotId}&page=0&size=20`, config);
        fetchedFiles = res.data || [];
      } catch { fetchedFiles = []; }
      let fetchedAll: ServiceType[] = [];
      try {
        const res = await axios.get(`${API_CONFIG.PLOT_SERVICE_REQ}?contractorTypesId.equals=${contractorId}&page=0&size=105`, config);
        fetchedAll = res.data || [];
        setAllServices(fetchedAll);
      } catch { setAllServices([]); }
      if (fetchedServices.length && fetchedAll.length) {
        const selectedIds = fetchedServices.map(s => s.servicesReq.id);
        setUnselected(fetchedAll.filter(s => !selectedIds.includes(s.id)));
      } else {
        setUnselected([]);
      }
      if (fetchedServices.length) {
        setPlot({
          name: fetchedServices[0]?.createdBy.firstName || userProfile?.user.firstName || "",
          plotSize: fetchedServices[0]?.plot.plotSize ? `${fetchedServices[0].plot.plotSize} sq` : "",
          location: fetchedServices[0]?.plot.plotLocation || "",
          days: "",
          squareFeet: fetchedServices[0]?.plot.plotSize ? `${fetchedServices[0].plot.plotSize} sq ft` : "",
          services: fetchedServices,
          files: fetchedFiles,
        });
      } else {
        setPlot(null);
      }
      setFetching(false);
    }
  }, [plotId, userProfile, loading, selectedService]);

  if (fetching || loading)
    return <div className="loader-container"><div className="spinner"></div><p>Loading, please wait...</p></div>;
  if (!plot)
    return <Box sx={{ maxWidth: "2000px", margin: "0 auto" }}><NavigationBar /><Typography sx={{ textTransform: "capitalize" }}>No data available</Typography><Footer /></Box>;

  const handleApprove = async () => {
    if (isLoading) return;
    if (!selectedPlan) {
      toast.error("Select a plan first!");
      return;
    }
    const token = localStorage.getItem("id_token");
    if (!token) {
      toast.error("Not logged in!");
      return;
    }
    setIsLoading(true);
    try {
      await axios.post(
        API_CONFIG.APPROVE_RATECARDS,
        {
          ...selectedPlan,
          isApproved: true,
          isDeleted: false,
          approvalRemarks: "Approved by user",
          user: userProfile?.user,
          plot: plotId ? { id: Number(plotId) } : undefined,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Plan approved!");
      navigate(`/mileStones/${plotId}?plotId=${plotId}`);
    } catch {
      toast.error("Failed to approve plan.");
    } finally {
      setIsLoading(false);
    }
  };


  const handleViewClick = (plotId: number, contractorType: ContractorType, companyProfile: { id: number } | null, isRateCardApproved: boolean, plotRateCards?: any[]) => {
    console.log('handleViewClick called with:', { plotId, contractorType, companyProfile, isRateCardApproved, plotRateCards });
    setSelectedPlotId?.(plotId);
    if (!isRateCardApproved) {
      // console.log('Navigating to customer-requirements:', plotId, contractorType, plotRateCards);
      navigate(`/customer-requirements/${plotId}`, { state: { selectedService: contractorType, plotRateCards: plotRateCards || [] } });
      return;
    }
    if (!companyProfile?.id) {
      // console.log('Navigating to customer-requirements (no companyProfile):', plotId, contractorType, plotRateCards);
      navigate(`/customer-requirements/${plotId}`, { state: { selectedService: contractorType, plotRateCards: plotRateCards || [] } });
      return;
    }
    const companyProfileId = companyProfile.id;
    // console.log('Navigating to milestone:', companyProfileId, plotId);
    navigate(`/milestone/${companyProfileId}?plotId=${plotId}`);
  };


  function getUserIdLogin(loggedInUser: any) {
    if (!loggedInUser) return undefined;
    return {
      id: loggedInUser.id,
      login: loggedInUser.login || '',
      firstName: loggedInUser.firstName || '',
      email: loggedInUser.email || ''
    };
  }

  function cleanObject(obj: any): any {
    if (Array.isArray(obj)) {
      return obj.map(cleanObject);
    } else if (obj && typeof obj === 'object') {
      return Object.entries(obj).reduce((acc, [key, value]) => {
        if (value !== undefined) {
          acc[key] = cleanObject(value);
        }
        return acc;
      }, {} as any);
    }
    return obj;
  }


  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const newFiles = Array.from(e.target.files);
    setIsUploading(true);
    const uploadedFiles: { file: File; url: string; previewUrl: string }[] = [];
    for (const file of newFiles) {
      if (file.size <= 10 * 1024 * 1024) {
        const formData = new FormData();
        formData.append("file", file);
        try {
          const token = localStorage.getItem("id_token");
          const response = await axios.post(API_CONFIG.FILE_UPLOAD, formData, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          });
          uploadedFiles.push({
            file,
            url: response.data,
            previewUrl: URL.createObjectURL(file),
          });
        } catch {
          toast.error("Failed to upload file");
        }
      } else {
        toast.error(`File ${file.name} exceeds the 10MB limit`);
      }
    }
    setFiles((prev) => [...prev, ...uploadedFiles]);
    if (uploadedFiles.length > 0) {
      setUploadSuccess(true);
      toast.success("Image uploaded successfully!");
      setTimeout(() => setUploadSuccess(false), 3000);
    }
    setIsUploading(false);
  };

  const handleRemoveFile = (index: number) => {
    setFiles((prev) => {
      const newFiles = [...prev];
      URL.revokeObjectURL(newFiles[index].previewUrl);
      newFiles.splice(index, 1);
      return newFiles;
    });
    toast.success("File removed");
  };


  const handleOpenPaymentModal = () => {
    if (selectedPlan) {
      setBookingAmount(
        selectedPlan.bookingAmount?.toString() ||
        selectedPlan.negotiatedPrice?.toString() ||
        ''
      );
    }
    setPaymentDate(todayDate); // Set payment date to current date
    setOpenPaymentModal(true);
  };

  const handleSavePayment = async () => {
    if (!bookingAmount || !paymentDate || !paymentMode || !loggedInUser || !selectedPlan) {
      toast.error("Missing payment details");
      return;
    }
    // if (files.length === 0) {
    //   toast.error("Upload Payment Proof is required");
    //   return;
    // }
    const amount = parseFloat(bookingAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error("Invalid payment amount");
      return;
    }
    const imageUrl = files.length > 0 ? files[0].url : '';
    const token = localStorage.getItem("id_token");

    if (!selectedPlan.isApproved) {
      try {
        await axios.post(
          API_CONFIG.APPROVE_RATECARDS,
          {
            ...selectedPlan,
            isApproved: true,
            isDeleted: false,
            approvalRemarks: "Approved by user",
            user: userProfile?.user,
            plot: plotId ? { id: Number(plotId) } : undefined,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch {
        toast.error("Failed to approve plan.");
        return;
      }
    }

    const payload = {
      plotId: plotId ? Number(plotId) : 0,
      plotRateCard: {
        id: Number(selectedPlan.id),
        user: selectedPlan.user ? { id: Number(selectedPlan.user.id) } : undefined,
        plot: selectedPlan.plot ? { id: Number(selectedPlan.plot.id) } : undefined,
        bookingAmount: Number(selectedPlan.bookingAmount),
      },
      companyId: plotMainData?.companyProfile?.id ? Number(plotMainData.companyProfile.id) : 0,
      stage: "string",
      remarks: "string",
      payment: {
        paymentAmount: amount,
        paymentMode,
        paymentRefernce: paymentReferenceId,
        craetedDate: new Date().toISOString(),
        remarks: "string",
        description: "string",
        imgUrl: imageUrl,
        status: "SUBMITTED",
        extraRemarks: "string",
        paymentDate: new Date(paymentDate).toISOString(), 
        user: getUserIdLogin(loggedInUser), 
      },
      paymentStatus: "SUBMITTED",
      approvalStatus: "APPROVED",
    };
    const cleanedPayload = cleanObject(payload);
    console.log('PAYLOAD SENT TO API:', JSON.stringify(cleanedPayload, null, 2));
    try {
      await axios.post(API_CONFIG.BOOKING_PAYMENT, cleanedPayload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      toast.success("Payment saved successfully");
      setOpenPaymentModal(false);
      setBookingAmount("");
      setPaymentDate("");
      setPaymentMode("");
      setPaymentReferenceId("");
      setFiles([]);
      setUploadSuccess(false);
      navigate("/booking", { state: { selectedTab: "In Progress", plotId, selectedPlan } });
    } catch (error) {
      toast.error("Failed to save payment");
    }
  };

  const handleNextClick = () => {
    if (!selectedPlan) {
      toast.error("Select a plan first!");
      return;
    }
    if (selectedPlan.isApproved) {
      navigate(`/mileStones/${plotId}?plotId=${plotId}`);
      return;
    }
    handleOpenPaymentModal();
  };

  const fromInProgress = location.state?.selectedTab === 'In Progress';
  const handleViewMilestone = () => {
    if (plotId) {
      navigate(`/mileStones/${plotId}?plotId=${plotId}`);
    }
  };

  return (
    <>
      <Box sx={{ maxWidth: "2000px", margin: "0 auto" }}>
        <NavigationBar />
        {fromInProgress && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Button variant="contained" color="primary" onClick={handleViewMilestone} sx={{ mb: 2 }}>
              View Milestone
            </Button>
          </Box>
        )}
        <Box
          sx={{
            py: { xs: 2, sm: 3, md: 3, lg: 3, xl: 3 },
            px: { xs: 1, sm: 0.8, md: 2, lg: 4, xl: 6 },
          }}
        >
          <Box sx={styles.mainContainer}>
            <Box sx={styles.header}>
              <Typography
                sx={{ ...styles.headerTypography, textTransform: "capitalize" }}
              >
                Customer Requirement
              </Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Link to="/booking">
                <img
                alt=""
                  src={require('../../icon/fi_11785255.svg')}
                  style={{
                    height: "30px",
                    marginRight: "15px",
                    marginTop: "10px",
                  }}
                />
              </Link>
            </Box>
            <Box sx={styles.mainBox}>
              <Box sx={styles.borderBox}>
                <Box sx={{ padding: { xs: 0.5, sm: 2, md: 1 } }}>
                  <Box sx={{ maxWidth: "700px", position: "relative" }}>
                    <Box
                      sx={{
                        pt: { xs: 1, md: 2.7 },
                        px: { xs: 2, sm: 1, md: 3, lg: 7, xl: 10 },
                      }}
                    >
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Typography
                            sx={{
                              ...styles.label,
                              textTransform: "capitalize",
                            }}
                          >
                            Name
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography
                            sx={{
                              ...styles.value,
                              textTransform: "capitalize",
                            }}
                          >
                            {plot?.name || "Not available"}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography
                            sx={{
                              ...styles.label,
                              textTransform: "capitalize",
                            }}
                          >
                            Plot Size
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography
                            sx={{
                              ...styles.value,
                              textTransform: "capitalize",
                            }}
                          >
                            {plot?.plotSize || "Not available"}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography
                            sx={{
                              ...styles.label,
                              textTransform: "capitalize",
                            }}
                          >
                            Location
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography
                            sx={{
                              ...styles.value,
                              textTransform: "capitalize",
                            }}
                          >
                            {plot?.location || "Not available"}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography
                            sx={{
                              ...styles.label,
                              textTransform: "capitalize",
                            }}
                          >
                        Square Feet
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography
                            sx={{
                              ...styles.value,
                              textTransform: "capitalize",
                            }}
                          >
                            {plot?.squareFeet || "Not available"}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Box sx={{ mt: 2 }}>
                        <Typography
                          sx={{
                            fontSize: { xs: "20px", sm: "20px", md: "20px" },
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            fontWeight: 500,
                            textTransform: "capitalize",
                          }}
                        >
                          Requirements
                        </Typography>
                        <FormGroup>
                          {plot?.services.length > 0 ? (
                            plot.services.map((service: PlotService) => (
                              <Box
                                key={service.id}
                                sx={{
                                  gap: 1,
                                  display: "flex",
                                  alignItems: "center",
                                  margin: "-3px",
                                }}
                              >
                                <Checkbox
                                  sx={{
                                    color: "black",
                                    "&.Mui-checked": { color: "black" },
                                    cursor: "default",
                                  }}
                                  checked
                                  disabled
                                />
                                <Typography
                                  sx={{
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    fontSize: {
                                      xs: "14px",
                                      sm: "15px",
                                      md: "15px",
                                      lg: "16px",
                                      xl: "16px",
                                    },
                                    color: "#212121",
                                    ml: "-3px",
                                    textTransform: "capitalize",
                                  }}
                                >
                                  {service.remarks ||
                                    service.servicesReq?.name ||
                                    service.description ||
                                    "No requirements"}
                                </Typography>
                              </Box>
                            ))
                          ) : (
                            <Typography sx={{ textTransform: "capitalize" }}>
                              No requirements available
                            </Typography>
                          )}
                        </FormGroup>
                        {(isRM || isUser) && (
                          <Box sx={{}}>

                            <FormGroup>
                              {unselected.length > 0 ? (
                                unselected.map(
                                  (service: ServiceType) => (
                                    <Box
                                      key={service.id}
                                      sx={{
                                        gap: 1,
                                        display: "flex",
                                        alignItems: "center",
                                        margin: "-3px",
                                      }}
                                    >
                                      <Checkbox
                                        sx={{
                                          color: "black",
                                          "&.Mui-checked": { color: "black" },
                                          cursor: "default",
                                        }}
                                        checked={false}
                                        disabled
                                      />
                                      <Typography
                                        sx={{
                                          whiteSpace: "nowrap",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          fontSize: {
                                            xs: "14px",
                                            sm: "15px",
                                            md: "15px",
                                            lg: "16px",
                                            xl: "16px",
                                          },
                                          color: "#212121",
                                          ml: "-3px",
                                          textTransform: "capitalize",
                                        }}
                                      >
                                        {service.name || "No requirements"}
                                      </Typography>
                                    </Box>
                                  )
                                )
                              ) : (
                                <Typography
                                  sx={{ textTransform: "capitalize" }}
                                >
                                  All available requirements are selected
                                </Typography>
                              )}
                            </FormGroup>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ px: { xs: 2, sm: 3, md: 3, lg: 7, xl: 10 } }}>
                  <Typography
                    sx={{
                      fontSize: { xs: "20px", sm: "20px", md: "20px" },
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      fontWeight: 500,
                      mb: 1,
                      textTransform: "capitalize",
                    }}
                  >
                    Images
                  </Typography>
                  {/* <Grid container spacing={2}>
                    {displayFiles.length > 0 ? (
                      displayFiles.map((file: PlotFile) => (
                        <Grid item xs={6} sm={6} md={3} lg={3} key={file.id}>
                          <Box
                            component="img"
                            src={file.fileUrl || CustomerReqImg}
                            alt={` No images available`}
                            sx={{
                              width: "100%",
                              borderRadius: "8px",
                              height: "200px",
                              objectFit: "cover",
                              mb:4
                            }}
                          />
                        </Grid>
                      ))
                    ) : (
                      <Grid item xs={6} sm={6} md={3} lg={3}>
                        <Box
                          sx={{
                            mb:2,
                            width: "100%",
                            height: "200px", 
                            border: "1px solid #ccc",
                            borderRadius: "8px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "#f9f9f9",
                          }}
                        >
                          <Typography
                            sx={{
                              textTransform: "capitalize",
                              fontSize: "16px",
                              color: "#666",
                              textAlign: "center",
                            }}
                          >
                            No images available
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                  </Grid> */}

                  <Grid container spacing={2}>
                    {plot?.files?.length > 0 ? (
                      plot.files.map((file: any) => (
                        <Grid item xs={12} sm={6} md={4} lg={4} key={file.id}>
                          <Box component="img" src={file.fileUrl || CustomerReqImg} alt={` No images available`} sx={{ width: "100%", borderRadius: "8px", height: "200px", objectFit: "cover", mb: 4 }} />
                        </Grid>
                      ))
                    ) : (
                      <Grid item xs={12} sm={12} md={4} lg={4} >
                        <Box sx={{ mb: 2, width: "100%", height: "200px", border: "1px solid #ccc", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#f9f9f9" }}>
                          <Typography sx={{ textTransform: "capitalize", fontSize: "16px", color: "#666", textAlign: "center" }}>No images available</Typography>
                        </Box>
                      </Grid>
                    )}
                  </Grid>

                </Box>
      
                {isUser && planCards.length > 0 && (
                  <Box sx={{ px: { xs: 2, sm: 3, md: 3, lg: 7, xl: 10 }, mt: 2 }}>
                    <Typography sx={{ fontSize: '20px', fontWeight: 500, mb: 2, textTransform: 'capitalize' }}>Estimate</Typography>
                    <Grid container spacing={2}>
                      {planCards.map((card: any, idx: number) => {
                        const isApproved = card.isApproved === true;
                        const isSelected = selectedPlan && selectedPlan.id === card.id;
                        const disableAll = planCards.some((c: any) => c.isApproved === true);
                        return (
                          <Grid item xs={12} sm={6} md={4} lg={4} key={card.id || idx}>
                            <Card
                              elevation={0}
                              sx={{
                                border: isSelected ? '2px solid #000' : '0.6px solid #161717',
                                borderRadius: '10px',
                                cursor: disableAll ? 'not-allowed' : 'pointer',
                                opacity: disableAll && !isSelected ? 0.6 : 1,
                                minHeight: "450px",
                                display: "flex",
                                flexDirection: "column"
                                // boxShadow: isSelected ? '0 0 8px #89652A55' : undefined,
                                // transition: 'border 0.2s, box-shadow 0.2s',
                              }}
                              onClick={() => {
                                if (disableAll) return; 
                                setSelectedPlan(card);
                              }}
                            >
                              <CardContent sx={{ 
                                height: "100%",
                                display: "flex", 
                                flexDirection: "column",
                                justifyContent: "space-between",
                                padding: "16px"
                              }}>
                                <Box>
                                  <Typography sx={{ 
                                    fontSize: '20px', 
                                    fontWeight: 600, 
                                    border: '0.2px solid #161717', 
                                    maxWidth: 'fit-content', 
                                    padding: '1px 25px', 
                                    borderRadius: '20px', 
                                    margin: '0 auto', 
                                    textTransform: 'capitalize',
                                    mb: 2
                                  }}>
                                    {card.companyRates?.rateCardTypes?.name || card.companyRates?.name || card.name || "Plan"}
                                  </Typography>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    <Typography sx={{ fontSize: '40px', fontWeight: 600, textAlign: 'center' }}>
                                      ₹{card.negotiatedPrice || card.companyRates?.price || 'N/A'}
                                    </Typography>
                                    <Typography sx={{ 
                                      fontSize: '20px', 
                                      fontWeight: 500, 
                                      textAlign: 'center', 
                                      textTransform: 'capitalize',
                                      mb: 2
                                    }}>
                                      Per Square Feet
                                    </Typography>
                                  </Box>
                                </Box>
                            
                                <Box sx={{ 
                                  flexGrow: 1, 
                                  display: "flex", 
                                  flexDirection: "column",
                                  justifyContent: "center"
                                }}>
                                  {card.companyRates?.features && card.companyRates.features.length > 0 ? (
                                    <>
                                      <Box sx={{ mb: 2 }}>
                                        {card.companyRates.features.slice(0, 4).map((feature: any, fidx: number) => (
                                          <Typography key={fidx} sx={{ 
                                            fontSize: { xs: '14px', sm: '15px', md: '15px', lg: '16px', xl: '16px' }, 
                                            fontWeight: 400, 
                                            textAlign: 'start', 
                                            display: 'flex', 
                                            gap: { xs: '8px', sm: '7px', md: '7px', lg: '8px', xl: '9px' }, 
                                            textTransform: 'capitalize',
                                            mb: 1,
                                            alignItems: 'center'
                                          }}>
                                            <CheckCircleIcon sx={{ fontSize: '18px', color: '#000' }} />
                                            {feature.name}
                                          </Typography>
                                        ))}
                                      </Box>
                                    </>
                                  ) : null}
                                
                                  {card.rateCardItems && card.rateCardItems.length > 0 ? (
                                    <>
                                      <Box sx={{ mb: 2 }}>
                                        {(expandedCard === card.id ? card.rateCardItems : card.rateCardItems.slice(0, 4)).map((item: any, idx: number) => (
                                          <Typography key={item.id || idx} sx={{ 
                                            fontSize: { xs: '14px', sm: '15px', md: '15px', lg: '16px', xl: '16px' }, 
                                            fontWeight: 400, 
                                            textAlign: 'start', 
                                            display: 'flex', 
                                            gap: { xs: '8px', sm: '7px', md: '7px', lg: '8px', xl: '9px' }, 
                                            textTransform: 'capitalize',
                                            mb: 1,
                                            alignItems: 'center'
                                          }}>
                                            <CheckCircleIcon sx={{ fontSize: '18px', color: '#000' }} />
                                            {item.rateCardItems?.name || 'N/A'}
                                          </Typography>
                                        ))}
                                        {card.rateCardItems.length > 4 && (
                                          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 'auto' }}>
                                            <Button
                                              size="small"
                                              sx={{ 
                                                textTransform: 'none',
                                                color: '#89652A',
                                                fontSize: '14px',
                                                fontWeight: 500,
                                                '&:hover': {
                                                  backgroundColor: 'rgba(137, 101, 42, 0.1)'
                                                }
                                              }}
                                              onClick={e => {
                                                e.stopPropagation();
                                                setExpandedCard(expandedCard === card.id ? null : card.id);
                                              }}
                                            >
                                              {expandedCard === card.id ? 'View Less' : 'View More'}
                                            </Button>
                                          </Box>
                                        )}
                                      </Box>
                                    </>
                                  ) : (
                                    <Typography sx={{ 
                                      fontSize: { xs: '14px', sm: '15px', md: '15px', lg: '16px', xl: '16px' }, 
                                      fontWeight: '400', 
                                      textAlign: 'center', 
                                      textTransform: 'capitalize',
                                      color: '#666'
                                    }}>
                                      No specifications available
                                    </Typography>
                                  )}
                                </Box>
                              </CardContent>
                            </Card>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                )}

                {(isRM || isUser) && (
                  <Box sx={styles.submitContainer}>
                    {isRM && (
                      <>
                        <Link to="/contractors" state={plot && plotId ? { booking: { plot: { ...plot, id: plotId }, files, services, unselected } } : undefined}>
                          <Button
                            sx={{ ...styles.submitButton, textTransform: "capitalize" }}
                            disabled={!plot || !plotId}
                            onClick={e => {
                              if (!plot || !plotId) {
                                e.preventDefault();
                                window.alert("Plot not found. Please go back and select a valid plot.");
                              }
                            }}
                          >
                            Next
                          </Button>
                        </Link>
                      </>
                    )}
                    {isUser && plotId && (
                      <Button
                        sx={{
                          ...styles.submitButton,
                          textTransform: "capitalize",
                        }}
                        onClick={() => {
                          if (fromInProgress) {
                            handleViewMilestone();
                            return;
                          }
                          handleNextClick();
                        }}
                        disabled={isLoading || (!selectedPlan && !fromInProgress)}
                      >
                        {isLoading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : fromInProgress ? 'Next' : 'Next'}
                      </Button>
                    )}
                  </Box>
                )}
            
       
                {/* {isProvider && plotId && plotMainData && (
                  (() => { console.log('[DEBUG] Rendering Provider Next Button:', { isProvider, plotId, plotMainData }); return null; })()
                )} */}
                {isProvider && plotId && plotMainData && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 ,mb:4}}>
                    <Button
                      sx={{ ...styles.submitButton, textTransform: 'capitalize' }}
                      onClick={() => {
                        handleViewClick(
                          Number(plotId),
                          selectedService,
                          plotMainData.companyProfile || null,
                          plotMainData.isRateCardApproved || false,
                          plotMainData.plotRateCards || []
                        );
                      }}
                    >
                      Next
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
        <Footer />

      </Box>
      <Modal
  open={openPaymentModal}
  onClose={() => {
    setOpenPaymentModal(false);
    setBookingAmount("");
    setPaymentDate("");
    setPaymentMode("");
    setPaymentReferenceId("");
    setFiles([]);
    setUploadSuccess(false);
  }}
  aria-labelledby="payment-modal-title"
>
  <Box
    sx={{
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      width: { xs: "80%", sm: 400, md: 600 },
      maxHeight: "80vh",
      overflowY: "auto",
      my: 2,
      bgcolor: "white",
      borderRadius: "10px",
      boxShadow: 24,
      p: 4,
      outline: "none",
    }}
  >
    <Box sx={{ mb: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
        }}
      >
        <Typography
          id="payment-modal-title"
          variant="h6"
          component="h2"
          sx={modalTitleStyle}
        >
          Payment Details
        </Typography>
        <IconButton
          onClick={() => setOpenPaymentModal(false)}
          sx={{ bgcolor: "#f4f4f4", color: "#89652A", "&:hover": { bgcolor: "#f4f4f4" } }}
        >
          <CloseIcon sx={{ color: "#89652A" }} />
        </IconButton>
      </Box>
      <Box sx={paymentPercentBoxStyle}>
        <PaymentIcon sx={{ color: "#89652A", mr: 1 }} />
        <Typography sx={paymentPercentTextStyle}>
          Booking Amount: ₹{selectedPlan?.bookingAmount || selectedPlan?.negotiatedPrice || 0}
        </Typography>
      </Box>
    </Box>
    <Box component="form" sx={{ width: "100%" }}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12} md={6} lg={6}>
          <Typography sx={labelStyle}>Amount</Typography>
          <TextField
            variant="outlined"
            placeholder="Enter amount"
            size="medium"
            type="number"
            value={bookingAmount}
            onChange={(e) => setBookingAmount(e.target.value)}
            fullWidth
            sx={contactTextField}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={6}>
          <Typography sx={labelStyle}>Payment Date</Typography>
          <TextField
            variant="outlined"
            size="medium"
            type="date"
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            sx={{
              ...contactTextField,
              '& .MuiInputBase-root': {
                backgroundColor: '#f5f5f5',
                color: '#666'
              }
            }}
            inputProps={{ 
              min: todayDate,
              max: todayDate,
              readOnly: true
            }}

            disabled={true}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={6}>
          <Typography sx={labelStyle}>Select Payment Mode</Typography>
          <TextField
            select
            value={paymentMode}
            onChange={(e) => setPaymentMode(e.target.value)}
            variant="outlined"
            size="medium"
            fullWidth
            sx={contactTextField}
          >
            <MenuItem value="" disabled>
              Payment Mode
            </MenuItem>
            <MenuItem value="UPI">UPI</MenuItem>
            <MenuItem value="Cash">Cash</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={6}>
          <Typography sx={labelStyle}>Payment Reference ID</Typography>
          <TextField
            size="medium"
            variant="outlined"
            placeholder="Payment Reference ID"
            value={paymentReferenceId}
            onChange={(e) => setPaymentReferenceId(e.target.value)}
            fullWidth
            sx={contactTextField}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={8} lg={8} sx={{ mx: "auto" }}>
          <Typography sx={labelStyle}>Upload Payment Proof</Typography>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              accept=".jpg,.jpeg,.png,.pdf"
              onChange={handleFileChange}
            />
            <Box onClick={() => fileInputRef.current?.click()} sx={uploadBoxDetailsAddStyleFullWidth}>
              <img src={Uploadicon} alt="Upload" style={{ height: "44px", margin: "8px 0" }} />
              <Typography sx={{ fontSize: "15px", fontWeight: 500, color: "#89652A", mb: 0.5 }}>Browse File</Typography>
              <Typography sx={{ fontSize: "13px", color: "#666" }}>Max 10 MB (.jpg, .png, .pdf, .jpeg)</Typography>
              {uploadSuccess && (
                <Chip icon={<CheckCircleIcon />} label="File uploaded successfully!" color="success" variant="outlined" sx={{ mt: 1, mx: "auto" }} />
              )}
            </Box>
            {files.length > 0 && (
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)" }, gap: 2, mb: 3 }}>
                {files.map((fileObj: { file: File; url: string; previewUrl: string }, index: number) => (
                  <Box key={index} sx={{ position: "relative", borderRadius: "12px", overflow: "hidden", aspectRatio: "1/1", boxShadow: "0 4px 8px rgba(0,0,0,0.1)" }}>
                    <img src={fileObj.previewUrl} alt={`Uploaded ${index}`} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    <IconButton sx={{ position: "absolute", top: 8, right: 8, bgcolor: "rgba(0,0,0,0.6)", width: 28, height: 28, "&:hover": { bgcolor: "rgba(0,0,0,0.8)" } }} onClick={() => handleRemoveFile(index)}>
                      <CloseIcon sx={{ fontSize: 18, color: "white" }} />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Grid>
        <Grid item xs={12} sx={{ textAlign: "center" }}>
          <Button
            variant="contained"
            onClick={handleSavePayment}
            disabled={
              isUploading ||
              !bookingAmount ||
              !paymentDate ||
              !paymentMode ||
              !paymentReferenceId ||
              isNaN(parseFloat(bookingAmount)) ||
              parseFloat(bookingAmount) <= 0
            }
            size="large"
            sx={contactSubmitButton}
          >
            {isUploading ? (
              <CircularProgress size={24} sx={{ color: "white" }} />
            ) : (
              "Save Payment"
            )}
          </Button>
        </Grid>
      </Grid>
    </Box>
  </Box>
</Modal>
    </>
  );
};

export default CustomerRequirement;

const styles = {
  mainContainer: {
    borderRadius: { xs: "10px", sm: "10px" },
    boxShadow: "0px 0px 3px 0px #00000033",
  },
  header: {
    background: "linear-gradient(90deg, #89652A 0%, #B69D74 100%)",
    color: "#fff",
    padding: { xs: "10px 12px", sm: "14px 20px", md: "16px 24px" },
    borderTopLeftRadius: { xs: "6px", sm: "8px" },
    borderTopRightRadius: { xs: "6px", sm: "8px" },
  },
  label: {
    fontSize: { xs: "14px", sm: "15px", md: "15px", lg: "16px", xl: "16px" },
    fontWeight: { xs: 500, sm: 500, md: 500, lg: 500 },
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  value: {
    fontWeight: { xs: 500, sm: 500, md: 500, lg: 500 },
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    fontSize: { xs: "14px", sm: "15px", md: "15px", lg: "16px", xl: "16px" },
  },
  headerTypography: {
    fontSize: { xs: "20px", sm: "25px", md: "25px" },
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    fontWeight: 500,
    lineHeight: { xs: 1.4, sm: 1.5, md: 1.6 },
    color: "white",
  },
  mainBox: {
    px: { xs: "13px", sm: "20px", md: "30px", lg: "50px" },
    py: { xs: "13px", sm: "15px", md: "20px", lg: "20px" },
  },
  borderBox: {
    borderRadius: "10px",
    border: "0.75px solid #2D2D2D33",
    mb: 4,
  },
  submitContainer: {
    display: "flex",
    justifyContent: "center",
    mb: 6,
    mt: 3,
  },
  submitButton: {
    textTransform: "capitalize",
    fontSize: { xs: "16px", md: "16px" },
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    fontWeight: 600,
    borderRadius: "28px",
    color: "white",
    bgcolor: "#89652A",
    py: 0.9,
    px: { xs: 4, md: 6 },
  },
};


const modalTitleStyle = {
  fontSize: { xs: "17px", sm: "20px", md: "20px" },
  fontWeight: 500,
  lineHeight: 1.3,
  color: "#89652A",
  fontFamily: "Poppins, sans-serif",
};
const paymentPercentBoxStyle = {
  display: "flex",
  alignItems: "center",
  background: "#f8f6f2",
  borderRadius: "12px",
  padding: "7px 13px",
  mb: 2,
  boxShadow: "0 1px 4px rgba(182,157,116,0.07)",
  border: "1.2px solid #e7e1d2",
};
const paymentPercentTextStyle = {
  fontWeight: 600,
  fontSize: "15px",
  color: "#89652A",
  letterSpacing: 0.1,
};
const labelStyle = {
  fontSize: { xs: "13px", sm: "14px", md: "14px", lg: "15px", xl: "15px" },
  fontWeight: 500,
  color: "#2D2D2D",
  fontFamily: "Poppins, sans-serif",
  mb: 0.7,
};
const contactTextField = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    borderColor: "#89652A",
    bgcolor: "#f5f5f5",
    minHeight: 38,
    fontSize: "14px",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#B69D74",
      borderWidth: "1.2px",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#89652A" },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#89652A",
      borderWidth: "1.5px",
    },
  },
  "& .MuiInputBase-input": { padding: "10px 12px", fontSize: "14px" },
};
const contactSubmitButton = {
  textTransform: "capitalize",
  fontSize: { xs: "14px", md: "14px" },
  fontWeight: 600,
  borderRadius: "22px",
  color: "white",
  bgcolor: "#89652A",
  py: 0.5,
  px: { xs: 3, md: 4 },
  mt: 1.2,
  "&:hover": { bgcolor: "#6b4e20" },
  "&:disabled": { bgcolor: "#a88b5e" },
};
const uploadBoxDetailsAddStyleFullWidth = {
  border: "2px dashed #B69D74",
  borderRadius: "10px",
  p: 1.2,
  textAlign: "center",
  mb: 1.2,
  cursor: "pointer",
  bgcolor: "rgba(182, 157, 116, 0.07)",
  width: "100%",
  maxWidth: "100%",
  transition: "border-color 0.2s",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  "&:hover": { borderColor: "#89652A" },
};